import { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { message, Modal } from "antd";
import dayjs from "dayjs";
import useOptions from "features/masterData/hooks/useOptions";
import { generateRandomString } from "utils/helper";
import * as services from "../services/employeeApi";
import { setFamilyList, setIsLoading } from "../slices/employeeSlice";
import { useTranslation } from "react-i18next";

function useEmployeeFamily() {
  const dispatch = useDispatch();
  const params = useParams();
  const [isModalOpen, setModalOpen] = useState(false);
  const [dataDetail, setDataDetail] = useState(undefined);
  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  let { id } = useParams();
  let employeeId = id;
  const familyList = useSelector((state) => state.employee.familyList);
  const {
    relationshipOptions,
    occupationOptions,
    getRelationshipOptions,
    getOccupationOptions,


  } = useOptions();
  const [isEditing, setIsEditing] = useState(false);
  const { t, i18n } = useTranslation();
  const toParams = (values = {}) => {
    const modifiedValues = {
      ...values,
      dateOfBirth: values?.dateOfBirth ? dayjs(values?.dateOfBirth).format("YYYY-MM-DD") : null,
    };

    return modifiedValues;
  };


  const toInitialValues = (initialValues = {}) => {
    let dateOfBirth = initialValues?.dateOfBirth
      ? dayjs(initialValues?.dateOfBirth)
      : null;
    return {
      ...initialValues,
      fullName: initialValues?.fullName,
      relationship: initialValues?.relationship?.id,
      dateOfBirth: dateOfBirth,
      career: initialValues?.career?.id,
      mobileNumber: initialValues?.mobileNumber,
      id: initialValues?.id,
    };
  };

  const initialValues = useMemo(() => {
    return toInitialValues(dataDetail);
  }, [dataDetail]);

  const onEdit = (values) => {
    setIsEditing(true);
    setModalOpen(true);
    setDataDetail(values);
  };

  const onCreate = useCallback(() => {
    // setCurrentYearAccumulate({});
    setModalOpen(true);
    setDataDetail(undefined);
  }, []);

  const onCancel = useCallback(() => {
    // setCurrentYearAccumulate({});
    setModalOpen(false);
  }, []);

  // const onDelete = useCallback(
  //   (values) => {
  //     console.log(" save", values)
  //     Modal.confirm({
  //       title: "Confirmation",
  //       content: (
  //         <>
  //           <p>Are you sure confirm to edit ?</p>
  //         </>
  //       ),
  //       onOk() {
  //         console.log("updateFamilyInformation", values);
  //         updateFamilyInformation(values);
  //       },
  //       okText: "CONFIRM",
  //       cancelText: "CANCEL",
  //     });
  //   },
  //   []
  // );

  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t('dialog.confirmation.header'),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to {values?.id ? "edit" : "create"} ?</span>
          : <span>คุณแน่ใจหรือไม่ที่จะ {values?.id ? "แก้ไข" : "สร้างใหม่"} ?</span>
        ),
        onOk() {
          updateFamilyInformation(values)
        },
        okText: t('confirm'),
        cancelText: t('generalInfo.cancel'),
      });
    },
    [currentCompany,
      currentEmployee]
  );

  const updateFamilyInformation = useCallback(
    async (values) => {
      console.log(values)
      try {
        dispatch(setIsLoading(true));
        await services.updateFamilyInformation(
          toParams({
            ...values,
            relationshipId: values?.relationship,
            careerId: values?.career,
            familyId: values?.id,
            requestId: generateRandomString(),
            companyId: currentCompany,
            employeeId: employeeId,
          })
        );
        message.success(t("common.success"))
        setIsEditing(false);
        getFamilyInformation(
          id
        );
        setFamilyList({});
        setModalOpen(false);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const onDelete = useCallback(
    async (params = {}) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>"Are you sure confirm to delete ?</span>
          : <span>คุณยืนยันการลบใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const response = await services.deleteFamilyInformation({
              employeeId: id,
              actionId: currentEmployee,
              familyId: params?.id,

            });
            getFamilyInformation(
              id
            );
            setFamilyList({});
            setModalOpen(false);
            message.success(t("common.success"))
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [dispatch, id, params]
  );



  const getFamilyInformation = useCallback(
    async (employeeId) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getFamilyInformation({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId,
        });
        dispatch(setFamilyList((response.data || {}).family || []));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  useEffect(() => {
    if (params.id && currentCompany) getFamilyInformation(params.id);
  }, [currentCompany, params.id, getFamilyInformation]);

  useEffect(() => {
    getRelationshipOptions();
  }, [getRelationshipOptions]);

  useEffect(() => {
    getOccupationOptions({
      companyId: currentCompany,
      groupType: "CAREER",
    });
  }, [currentCompany, getOccupationOptions]);

  return {
    isLoading,
    familyList,
    dataDetail,
    isModalOpen,
    initialValues,
    occupationOptions,
    relationshipOptions,
    onEdit,
    onCreate,
    onSubmit,
    onCancel,
    onDelete,
    getFamilyInformation,
    updateFamilyInformation
  };
}

export default useEmployeeFamily;
